// Copyright (c) Bentley Systems
import { Dispatch } from "react";
import { Parameter } from "../Action/parameter";
import { CellVm } from "../Elist/ViewModels/cell-vm";
import { DynamicObject } from "./dynamic-object";
import { ErrorState, setIsError } from "../app/is-error-slice";
import { setformOpen } from "../app/form-open-slice";
import { EfFieldValidationTypeEnum } from "./enumerators";
import { DawComponentRoutePaths } from "./daw-component-route-paths";

export function checkInternalServerError(
  responseStatus: number,
  dispatch: Dispatch<any>,
  runId: string,
) {
  if (responseStatus === 500) {
    dispatch(
      setIsError({
        errorState: ErrorState.InternalServerError,
        activityId: runId,
      }),
    );
    dispatch(setformOpen({ isOpen: false }));
  }
}

export function setErrorState(
  responseStatus: number,
  dispatch: Dispatch<any>,
  activityId: string | undefined,
  silent: boolean = false,
) {
  switch (responseStatus) {
    case 404: {
      dispatch(
        setIsError({
          errorState: ErrorState.NotFound,
          activityId,
          silent,
        }),
      );
      break;
    }
    case 403: {
      dispatch(
        setIsError({
          errorState: ErrorState.Forbidden,
          activityId,
          silent,
        }),
      );
      break;
    }
    case 401: {
      dispatch(
        setIsError({
          errorState: ErrorState.Unauthorized,
          activityId,
          silent,
        }),
      );
      break;
    }
    case 500: {
      dispatch(
        setIsError({
          errorState: ErrorState.InternalServerError,
          activityId,
          silent,
        }),
      );
      break;
    }
    default: {
      dispatch(
        setIsError({
          errorState: ErrorState.OtherError,
          activityId,
          silent,
        }),
      );

      break;
    }
  }
  if (!silent) {
    dispatch(setformOpen({ isOpen: false }));
  }
}

/**
 * Reads an RFC 7807 ProblemDetails error body and returns the best human-readable message.
 * The DAW API returns `detail` (e.g. "Search term must be at least 3 characters long.") for
 * 400/404/409 outcomes and a neutral `title` for 403/500. Falls back to the HTTP status when
 * the body is absent or not JSON. Use this to surface errors inline (local error state / toast)
 * rather than via the global error page, so the user isn't navigated away.
 */
export const getErrorMessageFromResponse = async (response: Response): Promise<string> => {
  try {
    const problem = await response.json();
    const message: string =
      problem?.detail || problem?.title || `Request failed with status ${response.status}.`;
    // Defense-in-depth: the backend's DawApiExceptionHandler already strips this, but strip here
    // too in case some other endpoint ever returns a raw BadRequestException message untouched.
    return message.replace(/^Bad Request:\s*/i, "");
  } catch {
    return `Request failed with status ${response.status}.`;
  }
};

export function getRouteStringFromNodeType(nodeType: string) {
  switch (nodeType) {
    case "EL": {
      return "elist";
    }
    case "ET": {
      return "etree";
    }
    case "EF": {
      return "eform";
    }
    case "EA": {
      return "archive";
    }

    default:
      return "";
  }
}

export const customCellValueSort: any = (
  rowA: any,
  rowB: any,
  columnId: string,
  desc: boolean,
) => {
  const valueA = rowA.values[columnId].value;
  const valueB = rowB.values[columnId].value;
  if (!isNaN(+valueA) && !isNaN(+valueB)) {
    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
  } else {
    const defaultVal = desc ? "AAAAAAAAAAAA" : "ZZZZZZZZ";
    const x = (valueA ?? defaultVal).localeCompare(
      rowB.values[columnId].value ?? defaultVal,
    );
    return x;
  }
};

export const customCellStringSort: any = (
  rowA: any,
  rowB: any,
  columnId: string,
  desc: boolean,
) => {
  const valueA = rowA.values[columnId];
  const valueB = rowB.values[columnId];
  if (!isNaN(+valueA) && !isNaN(+valueB)) {
    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
  } else {
    const defaultVal = desc ? "AAAAAAAAAAAA" : "ZZZZZZZZ";
    const x = (valueA ?? defaultVal).localeCompare(
      rowB.values[columnId].value ?? defaultVal,
    );
    return x;
  }
};

export function getUrlBasedOnEnv(path: string) {
  return (
    (window.env?.VITE_API_URL ?? import.meta.env.VITE_API_URL) + path
  );
}
/**
 * from lt=(lower than equal) value lt= to
 *
 * for string return true only when from === value === to
 */
export function meetCondition(
  from: string | undefined | null,
  to: string | undefined | null,
  value: string | undefined | null,
): boolean {
  if (
    from === undefined ||
    from === null ||
    to === undefined ||
    to === null ||
    value === undefined ||
    value === null
  ) {
    return false;
  }

  const valueInt = parseInt(value, 10);
  if (from === to) {
    if (isNaN(valueInt)) {
      if (typeof value === "boolean") {
        return (value as boolean).toString() === from.toLowerCase();
      } else {
        return value === from;
      }
    } else {
      return valueInt.toString() === from;
    }
  } else {
    const fromInt = parseInt(from, 10);
    if (isNaN(fromInt)) {
      return false;
    }
    const toInt = parseInt(to, 10);
    if (isNaN(toInt)) {
      return false;
    }
    if (isNaN(valueInt)) {
      return false;
    }

    if (fromInt <= valueInt && valueInt <= toInt) {
      return true;
    } else {
      return false;
    }
  }
}

export function getPropertyOrDefaultValue(
  propertyValue: string | undefined,
  defaultValue: string,
): string {
  return propertyValue !== undefined &&
    propertyValue !== null &&
    propertyValue !== ""
    ? propertyValue
    : defaultValue;
}

/** finding values from string. e.g: "padding: 2px 4px;background-color:#FFD600;font-weight:bold;border:2px solid Black;" */
export function getCssValueFromString(
  wholeString: string,
  stringToFind: string,
): string {
  const regexStrToFind = `(^|;)${stringToFind}:`;
  let startIndex = wholeString.search(regexStrToFind);
  if (startIndex !== -1) {
    if (wholeString[startIndex] === ";") {
      startIndex = startIndex + 1;
    }
    const endIndex = wholeString.indexOf(";", startIndex);
    return wholeString.substring(
      startIndex + stringToFind.length + 1,
      endIndex,
    );
  } else {
    return "";
  }
}

export function getstyleFromString(
  styleString: string,
  center: boolean = false,
) {
  const background = getCssValueFromString(styleString, "background-color");
  const fontWeight = getCssValueFromString(styleString, "font-weight");
  const color = getCssValueFromString(styleString, "color");
  //const width = getCssValueFromString(styleString, "width");
  //const height = getCssValueFromString(styleString, "height");

  if (center) {
    return {
      background,
      fontWeight,
      color,
      //width: width,
      alignItems: "center",
      justifyContent: "center",
    };
  } else {
    return {
      background,
      fontWeight,
      color,
      //width: width,
    };
  }
}

export function getCheckBoxStyleFromString(cellVm: CellVm) {
  if (cellVm.style !== undefined && cellVm.style !== null) {
    const background = getCssValueFromString(cellVm.style, "background-color");
    const fontWeight = getCssValueFromString(cellVm.style, "font-weight");
    const color = getCssValueFromString(cellVm.style, "color");
    //const width = getCssValueFromString(styleString, "width");
    //const height = getCssValueFromString(styleString, "height");

    return {
      background,
      fontWeight,
      color,
      alignItems: "center",
      justifyContent: "center",
    };
  } else {
    return {
      alignItems: "center",
      justifyContent: "center",
    };
  }
}

/**return array of parameters from url string like:
 * area=22&nodeid=NOTIFCREATE&Asset=000000000010006261 */
export function getParametersFromUrlString(
  urlString: string | undefined,
): Parameter[] {
  const params: Parameter[] = [];
  if (urlString === undefined) {
    return params;
  } else {
    recursiveGetParamsFromString(urlString, params);
    return params;
  }

  function recursiveGetParamsFromString(
    urlStr: string,
    parameters: Parameter[],
  ) {
    const equalIndex = urlStr.indexOf("=");
    if (equalIndex !== -1) {
      const paramName = urlStr.substring(0, equalIndex);
      urlStr = urlStr.substring(equalIndex + 1);
      const andIndex = urlStr.indexOf("&");
      if (andIndex !== -1) {
        const paramValue = urlStr.substring(0, andIndex);
        parameters.push(new Parameter(paramName, paramValue));
        urlStr = urlStr.substring(andIndex + 1);
        recursiveGetParamsFromString(urlStr, parameters);
      } else {
        parameters.push(new Parameter(paramName, urlStr));
      }
    }
  }
}

/**translate parameters defined as [[parameterName]] to theirs values*/
export function getTranslatedStringFromParameters(
  stringToTranslate: string,
  parameters: Parameter[],
): string | undefined {
  let stringCopy = stringToTranslate.slice();
  const beginBracketsIndeces = getIndicesOf("[[", stringToTranslate);
  const endBracketsIndeces = getIndicesOf("]]", stringToTranslate);
  if (beginBracketsIndeces.length !== endBracketsIndeces.length) {
    return stringToTranslate;
  }
  for (let i = 0; i < beginBracketsIndeces.length; i++) {
    const paramName = stringToTranslate.substring(
      beginBracketsIndeces[i] + 2,
      endBracketsIndeces[i],
    );
    if (paramName !== undefined) {
      let parValue = "";
      const valueFromParameters = parameters.find((p) => p.name === paramName);
      if (valueFromParameters !== undefined) {
        parValue = valueFromParameters.value as string;
      }

      stringCopy = stringCopy.replace(`[[${  paramName  }]]`, parValue);
    }
  }
  return stringCopy;
}

export function getIndicesOf(
  searchStr: string,
  str: string,
  caseSensitive: boolean = false,
) {
  const searchStrLen = searchStr.length;
  if (searchStrLen === 0) {
    return [];
  }
  let startIndex = 0,
    index;
  const indices = [];
  if (!caseSensitive) {
    str = str.toLowerCase();
    searchStr = searchStr.toLowerCase();
  }
  while ((index = str.indexOf(searchStr, startIndex)) > -1) {
    indices.push(index);
    startIndex = index + searchStrLen;
  }
  return indices;
}

export function isEmptyObject(obj: object): boolean {
  return Object.keys(obj).length === 0;
}

export function getJsonFromParameters(params: Parameter[]): string {
  const parametersObject: DynamicObject = {};
  params.forEach((par) => {
    parametersObject[par.name as string] = par.value?.toString();
  });
  const jsonBody = JSON.stringify(parametersObject);
  return jsonBody;
}

export function GetUrlByNode(nodeType: string, nodeId: number): string {
  const routeString = getRouteStringFromNodeType(nodeType);
  let navigateStr = "/";
  if (routeString !== "") {
    navigateStr = `/${  routeString  }/${  nodeId}`;
  }
  return navigateStr;
}

export function GetUrlByUniqueName(uniqueName: string): string {
  return DawComponentRoutePaths[
    uniqueName as keyof typeof DawComponentRoutePaths
  ];
}

export const getQueryParam = (param: string): string | null => {
  if (window.location.search) {
    const queryParamsFromUrl = new URLSearchParams(window.location.search);
    for (const [key, value] of queryParamsFromUrl.entries()) {
      if (key.toLowerCase() === param.toLowerCase()) {
        return value;
      }
    }
  }
  return null;
};

export const parseStringToEfFieldValidationTypeEnum = (
  value: string,
): EfFieldValidationTypeEnum | undefined => {
  if (
    Object.values(EfFieldValidationTypeEnum).includes(
      value as EfFieldValidationTypeEnum,
    )
  ) {
    return value as EfFieldValidationTypeEnum;
  }
  return undefined;
};

const nameRegex =
  /(^[a-zA-Z0-9]+$)|(^[a-zA-Z0-9]+[ _\-a-zA-Z0-9\(\)\[\]]*[a-zA-Z0-9\)\]]+$)/;
const uniqueNameRegex =
  /(^[a-zA-Z0-9]+$)|(^[a-zA-Z0-9]+[_\-a-zA-Z0-9]*[a-zA-Z0-9]+$)/;
const versionRegex = /^\d+(\.\d+)*$/;

function validateXml(xmlString: string): boolean {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "application/xml");
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("Invalid XML");
    }
    return true;
  } catch {
    return false;
  }
}

// Sniffs format by first non-whitespace char; XML and JSON can't both start with "<".
export const isXmlContent = (value: string): boolean =>
  value.trimStart().startsWith("<");

export const validateField = (
  value: string,
  validationType: EfFieldValidationTypeEnum,
): boolean => {
  switch (validationType) {
    case EfFieldValidationTypeEnum.Default: {
      return true;
    }
    case EfFieldValidationTypeEnum.Email: {
      const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
      return emailRegex.test(value);
    }
    case EfFieldValidationTypeEnum.Url: {
      const urlRegex =
        /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/;
      return urlRegex.test(value);
    }
    case EfFieldValidationTypeEnum.Xml: {
      return validateXml(value);
    }
    case EfFieldValidationTypeEnum.Json: {
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }
    case EfFieldValidationTypeEnum.XmlOrJson: {
      if (isXmlContent(value)) {
        return validateXml(value);
      }
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }
    case EfFieldValidationTypeEnum.GuidType: {
      const guidRegex =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
      return guidRegex.test(value);
    }
    case EfFieldValidationTypeEnum.IntType: {
      return Number.isInteger(Number(value));
    }
    case EfFieldValidationTypeEnum.FloatType: {
      const floatRegex = /^\d+\.\d+$/;
      return floatRegex.test(value);
    }
    case EfFieldValidationTypeEnum.Boolean: {
      if (typeof value === "boolean") {
        return true;
      }

      if (typeof value === "string") {
        return (
          value.toLowerCase() === "true" || value.toLowerCase() === "false"
        );
      }

      return false;
    }
    case EfFieldValidationTypeEnum.Date: {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
    case EfFieldValidationTypeEnum.Datetime: {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
    case EfFieldValidationTypeEnum.Name100: {
      if (value.length > 100) {
        return false;
      }

      return nameRegex.test(value);
    }
    case EfFieldValidationTypeEnum.Name200: {
      if (value.length > 200) {
        return false;
      }
      return nameRegex.test(value);
    }
    case EfFieldValidationTypeEnum.Name150: {
      if (value.length > 150) {
        return false;
      }
      return nameRegex.test(value);
    }
    case EfFieldValidationTypeEnum.UniqueName: {
      if (value.length > 150) {
        return false;
      }
      return uniqueNameRegex.test(value);
    }
    case EfFieldValidationTypeEnum.MqttClientId: {
      const mqttClientIdRegex = /^[a-zA-Z][a-zA-Z0-9-_]*[a-zA-Z0-9]$/;
      return mqttClientIdRegex.test(value);
    }
    case EfFieldValidationTypeEnum.MqttKey: {
      // Base64 regex pattern
      const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
      // Check if the string matches the Base64 pattern
      if (!base64Regex.test(value)) {
        return false;
      }

      // Decode the Base64 string
      try {
        const decoded = atob(value);
        // Check if the decoded length is 32 bytes (256 bits)
        return decoded.length === 32;
      } catch {
        // If decoding fails, it's not a valid Base64 string
        return false;
      }
    }
    case EfFieldValidationTypeEnum.SystemId: {
      if (value.length < 3) {
        return false;
      }
      if (value.length > 200) {
        return false;
      }
      const systemIdRegex = /^[a-zA-Z][a-zA-Z0-9-_]*[a-zA-Z0-9]$/;
      return systemIdRegex.test(value);
    }
    case EfFieldValidationTypeEnum.Version: {
      if (value.length > 50) {
        return false;
      }
      return versionRegex.test(value);
    }
    case EfFieldValidationTypeEnum.SearchString: {
      // Check if the value starts with '*'
      if (value.startsWith("*")) {
        return false;
      }

      // Remove trailing '*' for length check
      let trimmed = value;
      if (trimmed.endsWith("*")) {
        trimmed = trimmed.slice(0, -1);
      }

      // Require at least 4 non-whitespace characters (do not count whitespace)
      const nonWhitespaceCount = trimmed.replace(/\s/g, "").length;
      if (nonWhitespaceCount < 4) {
        return false;
      }

      // Validate against the regex: haves to start with alphanumeric, can contain spaces, underscores, hyphens, and brackets, and must end with alphanumeric or closing bracket
      const regex =
        /(^[a-zA-Z0-9]+$)|(^[a-zA-Z0-9]+[ _\-a-zA-Z0-9\(\)\[\]]*[a-zA-Z0-9\)\]]+$)/;
      if (!regex.test(trimmed)) {
        return false;
      }

      return true;
    }
    default: {
      return false;
    }
  }
};

export function FormatDate(dateStr: any): React.ReactNode {
  if (dateStr !== undefined && dateStr !== null) {
    const date = new Date(dateStr);
    return date.toLocaleString();
  } else {
    return dateStr;
  }
}

export function getTotalHeightWithMargins(element: HTMLElement | null): number {
  if (!element) {
    return 0;
  } // Return 0 if the element is not provided or null
  const computedStyle = getComputedStyle(element);
  const height = element.getBoundingClientRect().height;
  const marginTop = parseFloat(computedStyle.marginTop);
  const marginBottom = parseFloat(computedStyle.marginBottom);
  return height + marginTop + marginBottom;
}

export function isGuid(value: string | null): boolean {
  const guidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return value !== null && guidRegex.test(value);
}
